import React from 'react';
import Layout from '@layouts/layout';
import SectionLayout from '@layouts/section-layout';
import algoliasearch from 'algoliasearch/lite';
import aa from 'search-insights';
import FilterBlock from '@blocks/filter-block/filter-block';
import StyledFilterMenu from '@components/filter-menu/filter-menu';
import UtilityBar from '@components/utility-bar/utility-bar';
import ContentHubResults from '../design-system/src/components/content-hub-results/content-hub-results';
import CustomSortBy from '../design-system/src/blocks/custom-sortby/custom-sortby';
import CustomStats from '../design-system/src/components/search/search-stats';
import StyledAppliedFiltersBlock from '../design-system/src/blocks/applied-filters-block/applied-filters-block';
import StyledSearchBox from '../design-system/src/components/search/search-box';
import StyledTopicsFilter from '../design-system/src/components/topics-filter/topics-filter';
import styled from '@emotion/styled/macro';
import vars from '@variables';

import { Configure, CurrentRefinements, InstantSearch } from 'react-instantsearch';
import { CustomSearchProvider } from '../design-system/src/components/search/CustomSearchContext';
import { TranslateUnconnected } from '../design-system/src/utils/translation-functions';
import { createNavbarLocaleLinks } from '../utils/generate-url-functions';
import { getFooterData } from '../utils/translation-functions';
import { history } from 'instantsearch.js/es/lib/routers';
import { orderBy } from 'lodash';

import {
  DelimitedArrayParam,
  StringParam,
  useQueryParam,
} from 'use-query-params';

aa('init', {
  appId: process.env.GATSBY_ALGOLIA_APP_ID,
  apiKey: process.env.GATSBY_ALGOLIA_API_KEY,
  useCookie: true,
});

const encodedRefinementsTitle = {
  resourceContentType: 'Content Type',
};

const searchClient = algoliasearch(
  process.env.GATSBY_ALGOLIA_APP_ID,
  process.env.GATSBY_ALGOLIA_API_KEY,
  process.env.GATSBY_ALGOLIA_INDEX_NAME
);

const ContentHubPage = ({ pageContext, className }) => {
  // Primary nav and footer data handling
  const footerData = getFooterData(pageContext.locale);
  const staticTranslations = pageContext.staticTranslations;

  const metaData = {
    title: pageContext.contentHubTitle,
    metaTitle: pageContext.contentHubMetaDataTitle,
    metaDescription: pageContext.contentHubMetaDataTitle,
    socialCardDescription: pageContext.contentHubMetaDataSocialCardDescription,
    localeCode: pageContext.locale,
  };

  const gtmDataLayerVars = {
    event: 'Page View',
    pageType: 'content hub',
  };
  const algoliaIndexName = process.env.GATSBY_ALGOLIA_INDEX_NAME;

  const encodedSorting = {
    dateDesc: `${algoliaIndexName}_date-desc`,
    dateAsc: `${algoliaIndexName}_date-asc`,
    aZ: `${algoliaIndexName}_A-Z`,
    zA: `${algoliaIndexName}_Z-A`,
    rel: `${algoliaIndexName}`,
  };

  const FILTERS = [
    'format',
    'topics.title',
    'resourceContentType.title',
    'geography.title',
  ];

  const encodedMenu = {
    [FILTERS[1]]: 'topics',
    [FILTERS[2]]: 'contentType',
    [FILTERS[3]]: 'geography',
  };

  const encodeSortByForURL = sortBy => {
    for (const [encodedSortKey, sortByValue] of Object.entries(
      encodedSorting
    )) {
      if (sortBy === sortByValue) {
        return encodedSortKey;
      }
    }
    return 'rel';
  };

  const decodeSortByFromURL = encodedSortKey => {
    return encodedSorting[encodedSortKey] || encodedSorting.dateDesc;
  };

  const encodedFilters = {
    AboutUs: 'About us',
    BuiltEnvironment: 'Built Environment',
    LearningResource: 'Learning resource',
    WorkshopResource: 'Workshop resource',
    CircularEconomyExplained: 'Circular economy explained',
    CaseStudy: 'Case study',
    LatinAmerica: 'Latin America',
    NorthAmerica: 'North America',
    ExternalResource: 'External Resource',
    PressRelease: 'Press Release',
  };

  function encodeFiltersForURL(searchValues) {
    const matchingKeys = [];
    for (const searchValue of searchValues) {
      let foundMatch = false;
      for (const key in encodedFilters) {
        if (encodedFilters[key] === searchValue) {
          matchingKeys.push(key);
          foundMatch = true;
          break;
        }
      }
      if (!foundMatch) {
        matchingKeys.push(searchValue);
      }
    }
    return matchingKeys.join('_');
  }

  const decodeFiltersFromURL = filterKeys => {
    const decodedFilters = [];
    for (const filterKey of filterKeys) {
      decodedFilters.push(
        encodedFilters.hasOwnProperty(filterKey)
          ? encodedFilters[filterKey]
          : filterKey
      );
    }

    return decodedFilters;
  };

  const [topics] = useQueryParam('topics', DelimitedArrayParam);
  const [sortBy] = useQueryParam('sortBy', StringParam);
  const [contentType] = useQueryParam('contentType', DelimitedArrayParam);
  const [geography] = useQueryParam('geography', DelimitedArrayParam);

  const routing = {
    router: history(),
    stateMapping: {
      stateToRoute(uiState) {
        const indexUiState = uiState[algoliaIndexName];
        const urlParams = {
          search: indexUiState.query,
          sortBy: encodeSortByForURL(indexUiState.sortBy),
          format: indexUiState.menu?.format,
        };

        if (!indexUiState.refinementList) return urlParams;

        Object.keys(encodedMenu).forEach(filterKey => {
          if (indexUiState.refinementList[filterKey]) {
            urlParams[encodedMenu[filterKey]] = encodeFiltersForURL(
              indexUiState.refinementList[filterKey]
            );
          }
        });

        return urlParams;
      },
      routeToState(routeState) {
        const newState = {
          [algoliaIndexName]: {
            query: routeState.search,
            menu: {
              format: routeState.format,
            },
            refinementList: {},
            page: routeState.page,
            sortBy: decodeSortByFromURL(sortBy),
          },
        };

        if (topics) {
          newState[algoliaIndexName].refinementList[FILTERS[1]] =
            decodeFiltersFromURL(topics);
        }

        if (contentType) {
          newState[algoliaIndexName].refinementList[FILTERS[2]] =
            decodeFiltersFromURL(contentType);
        }

        if (geography) {
          newState[algoliaIndexName].refinementList[FILTERS[3]] =
            decodeFiltersFromURL(geography);
        }

        return newState;
      },
    },
  };

  const navbarLocaleLinks = createNavbarLocaleLinks(
    [
      {
        enabled: true,
        node_locale: 'en-GB',
        pageType: 'basic',
        slug: 'explore',
      },
      {
        enabled: true,
        node_locale: 'es',
        pageType: 'basic',
        slug: 'explora',
      },
      {
        enabled: true,
        node_locale: 'pt',
        pageType: 'basic',
        slug: 'saiba-mais',
      },
    ],
    pageContext.enabledLocaleCodes
  );

  return (
    <Layout
      footerData={footerData}
      className={className}
      metaData={metaData}
      gtmDataLayerVars={gtmDataLayerVars}
      hasTopPadding={true}
      isContentHub={true}
      globals={pageContext?.globals ? pageContext.globals : null}
      navbarLocaleLinks={navbarLocaleLinks}
    >
      <SectionLayout isContentHub={true}>
        <ContentWrapper>
          <CustomSearchProvider>
            <InstantSearch
              indexName={algoliaIndexName}
              searchClient={searchClient}
              routing={routing}
              insights={true}
            >
              <Configure
                filters={`locale:${pageContext.locale}`}
                hitsPerPage="20"
                clickAnalytics
                userToken={'user-1'}
              />
              <StyledSearchBox
                title={pageContext.contentHubSearchBoxTitle}
                backgroundImageSrc={pageContext.contentHubSearchBoxBannerImage}
              />

              {pageContext.localeLinks.length > 0 && (
                <UtilityBar />
              )}
              <div className="inner-wrapper">
                <div className="filters-wrapper">
                  <FilterBlock>
                    <StyledFilterMenu
                      attribute="format"
                      className="filter-menu"
                    />
                  </FilterBlock>
                  <div className="dropdown-filters-wrapper">
                    <StyledTopicsFilter
                      label="Topic"
                      attribute="topics.title"
                      limit={40}
                      showMoreLimit={50}
                      showMore={true}
                      transformItems={items => orderBy(items, 'label', 'asc')}
                    />
                    <StyledTopicsFilter
                      label="Content type"
                      attribute="resourceContentType.title"
                      limit={40}
                      showMoreLimit={50}
                      showMore={true}
                      transformItems={items => orderBy(items, 'label', 'asc')}
                    />
                    <StyledTopicsFilter
                      label="Geography"
                      attribute="geography.title"
                      limit={40}
                      showMoreLimit={50}
                      showMore={true}
                      transformItems={items => orderBy(items, 'label', 'asc')}
                    />
                    <CustomSortBy
                      items={[
                        { value: `${algoliaIndexName}_A-Z`, label: 'A-Z' },
                        { value: `${algoliaIndexName}_Z-A`, label: 'Z-A' },
                        {
                          value: `${algoliaIndexName}_date-desc`,
                          label: TranslateUnconnected(
                            'Most recent',
                            staticTranslations
                          ),
                        },
                        {
                          value: `${algoliaIndexName}_date-asc`,
                          label: TranslateUnconnected(
                            'Least recent',
                            staticTranslations
                          ),
                        },
                        {
                          value: `${algoliaIndexName}`,
                          label: TranslateUnconnected(
                            'Most relevant',
                            staticTranslations
                          ),
                        },
                      ]}
                      staticTranslations={staticTranslations}
                    />
                  </div>
                </div>

                <StyledAppliedFiltersBlock>
                  <CurrentRefinements
                    transformItems={items => {
                      const transformedItems = items.map(item => {
                        if (Array.isArray(item.refinements)) {
                          const label = item.label
                            ? item.label.split('.')[0]
                            : '';
                          item.label = encodedRefinementsTitle[label] || label; // handles resourceContentType outlier
                          item.refinements = item.refinements.map(
                            refinement => {
                              return {
                                ...refinement,
                                label: TranslateUnconnected(
                                  refinement.label.trim(),
                                  staticTranslations
                                ),
                              };
                            }
                          );
                        } else {
                          item.label = item.label.split(':')[1];
                        }

                        return {
                          ...item,
                          label: TranslateUnconnected(
                            item.label?.trim(),
                            staticTranslations
                          ),
                        };
                      });
                      transformedItems.sort((a, b) => {
                        return (
                          FILTERS.indexOf(a.attribute) -
                          FILTERS.indexOf(b.attribute)
                        );
                      });

                      transformedItems.forEach(item => {
                        if (item.attribute === FILTERS[0]) {
                          item.label = item.refinements[0].label;
                          item.refinements[0].label = null;
                        }
                        return item;
                      });

                      return transformedItems;
                    }}
                  />
                </StyledAppliedFiltersBlock>
                <CustomStats />
                <ContentHubResults />
              </div>
            </InstantSearch>
          </CustomSearchProvider>
        </ContentWrapper>
      </SectionLayout>
    </Layout>
  );
};

const ContentWrapper = styled.div`
  border: 2px solid grey;
`;

const StyledContentHub = styled(ContentHubPage)`
  .inner-wrapper {
    // display: flex;
    height: 100%;
    max-width: ${vars.mainMaxWidth};
    margin: 0 auto ${vars.spacingXl};
    padding-left: ${vars.spacingL};
    padding-right: ${vars.spacingL};

    // flex-direction: column;
  }

  .filters-wrapper {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    width: 100%;

    @media screen and (max-width: ${vars.breakPointTablet}) {
      min-height: 25rem;
    }

    div {
    }

    .dropdown-filters-wrapper {
      display: flex;
      padding-top: 3rem;

      @media screen and (max-width: ${vars.breakPointTablet}) {
        display: flex;
        flex-wrap: wrap;
        padding-top: 0;
        justify-content: space-between;
        width: 100%;

        div {
          padding-bottom: 5rem;
        }
      }
      @media screen and (max-width: ${vars.breakPointSmallDesktop}) {
        min-height: 8rem;
        padding-top: 0;
      }
    }
  }

  ${SectionLayout} {
    .section-container {
      margin-top: 0;
    }
  }
  .ais-Hits .ais-Hits-list {
    list-style: none;
  }
  .refine-sort-block {
    margin-top: 1.5rem;
    display: flex;
    justify-content: space-between;
    flex-direction: column;
    margin-left: 0;
    @media screen and (min-width: calc(${vars.breakPointTenColumn} + 1px)) and (max-width: ${vars.breakPointTablet}) {
      flex-direction: row;
      margin-top: 1.6rem;
      width: 100%;
      flex-wrap: wrap;
      margin-left: 0;
    }
    @media screen and (min-width: ${vars.breakPointTablet}) {
      flex-direction: row;
      max-height: 5rem;
      width: 100%;
      margin-left: 0;
      justify-content: flex-start;
    }
    @media screen and (min-width: ${vars.breakPointSmallDesktop}) {
      margin-left: 1.5rem;
      justify-content: flex-end;
    }
  }
  .ais-RefinementList {
    position: relative;
    display: inline-block;
    margin-right: 0;
    width: 100%;
    ${vars.fontSize2}
    color: ${vars.policyGrey};
    .ais-RefinementList-list {
      width: 100%;
      border: 1px solid ${vars.borderMidGrey};
      position: absolute;
      z-index: 99;
      background-color: white;
      border-radius: 3px;
      list-style-type: none;

      .ais-RefinementList-item {
        min-height: 3.6rem;
        width: 15rem;
      }

      .ais-sortby-item {
        width: 100%;
        padding-top: 0.7rem;
        padding-bottom: 0.7rem;
        height: 3.6rem;

        button {
          font-weight: ${vars.fontWeightRegular};
          display: flex;
          justify-content: start;
          color: black;
          background-color: transparent;

          &:disabled {
            color: #ccc;
            cursor: default;
          }
        }
      }

      .ais-RefinementList-showMore {
        background-color: transparent;
        padding: 0;

        .ais-RefinementList-item-showMore {
          width: 100%;
          display: flex;
          justify-content: space-between;

          svg {
            margin-top: 0.5rem;
          }
        }

        span {
          font-weight: ${vars.fontWeightRegular};
        }
      }

      button {
        color: blue;
        width: 100%;
        height: 100%;
        border: 0;
        cursor: pointer;
      }
    }
    @media screen and (min-width: calc(${vars.breakPointTenColumn} + 1px)) and (max-width: ${vars.breakPointTablet}) {
      width: 47%;
      margin-right: 0;
    }
    @media screen and (min-width: ${vars.breakPointTablet}) {
      margin-right: 1.5rem;
      width: 16rem;

      &:last-of-type {
        margin-right: 0;
      }
    }
  }

  .ais-RefinementList li {
    padding: 8px;

    .ais-RefinementList-label {
      cursor: pointer;
      display: flex;
      justify-content: start;

      .ais-RefinementList-labelText {
        font-weight: ${vars.fontWeightRegular};
        margin-left: 0.5rem;
      }
    }
  }
  .ais-RefinementList-list li:nth-of-type(1) {
    font-weight: ${vars.fontWeightRegular};
    ${vars.fontSize2};
    line-height: 2.9rem;
    padding: ${vars.spacingS};
  }
  .ais-RefinementList-list li:nth-of-type(2) {
    padding-top: 1.2rem;
  }
  .ais-SortBy {
    display: inline-block;
    vertical-align: top;
    @media screen and (max-width: ${vars.breakPointTablet}) {
      width: 100%;
    }
  }
  .ais-SortBy-option {
    font-weight: ${vars.fontWeightRegular};
    font-size: 1.2rem;
  }

  .ais-InfiniteHits {
    padding-bottom: 3rem;
    .ais-InfiniteHits-list {
      list-style: none;
      padding: 0;
      margin: 0;
      display: flex;
      flex-wrap: wrap;
    }

    .ais-InfiniteHits-loadMore {
      display: block;
      min-width: 16rem;
      margin: ${vars.spacingS} auto;
      margin-bottom: 2rem;
      padding: 1.4rem 0;
      border: 1px solid ${vars.transparentBorderGrey};
      color: ${vars.coreDarkGrey};
      background: ${vars.white};
      ${vars.fontSize2}
      font-weight: ${vars.fontWeightBold};
      text-align: center;
      text-decoration: none;
      &:first-letter {
        text-transform: uppercase;
      }
      ${vars.primaryLinkHoverTransition};
      &:hover {
        background-color: ${vars.coreDarkGrey};
        color: ${vars.white};
        cursor: pointer;
      }
      &:focus {
        outline: none;
      }
      @media screen and (min-width: ${vars.breakPointTablet}) {
        margin: 0 auto;
      }
    }
    .ais-InfiniteHits-loadMore--disabled {
      display: none;
    }
  }
`;

export default StyledContentHub;
